import { PERMISSIONS } from '@/constants/permissions'
import { isDashboardPageVisible } from '@/constants/dashboardPages'
import {
  hasNavigationItemPermission,
  type HasPermissionFn,
} from '@/utils/navigationPermissionAccess'
import {
  canAccessAnySalesWorkflow,
  canAccessMyDrafts,
  canViewSubmitted,
  SALES_WORKFLOW_ROUTE_PERMISSION_BY_PATH as SW_ROUTE_MAP,
} from '@/utils/swDdAccess'

export type { HasPermissionFn }

/** @deprecated Renamed permissions still accepted until sessions refresh after migration. */
export const LEGACY_SALES_VIEW_PERMISSIONS = {
  dashboardView: 'sales.dashboard.view',
  openPipelineView: 'sales.pipeline.view',
  salesDetailView: 'sales.sale_detail.view',
} as const

/** Legacy dashboard view permissions accepted until roles are migrated to sales.dashboard.view */
export const LEGACY_SALES_DASHBOARD_VIEW_PERMISSIONS = [
  PERMISSIONS.SALES.DASHBOARD_OLD.VIEW,
  PERMISSIONS.SALES.PERFORMANCE.VIEW,
] as const

/** Legacy dashboard export permissions accepted until roles are migrated to sales.dashboard.export */
export const LEGACY_SALES_DASHBOARD_EXPORT_PERMISSIONS = [
  PERMISSIONS.SALES.PERFORMANCE.EXPORT,
] as const

/** Exact tile/submitted view check — no legacy umbrella (RBAC-SLICE-3/5). */
export function hasSalesWorkflowPagePermission(
  hasPermission: HasPermissionFn,
  pagePermission: string,
): boolean {
  return hasPermission(pagePermission)
}

/** @deprecated Route meta should use a single requiredPermission. */
export function salesWorkflowViewPermissionOptions(pagePermission: string): string[] {
  return [pagePermission]
}

export function hasCanonicalOrLegacyPermission(
  hasPermission: HasPermissionFn,
  canonical: string,
  legacy?: string
): boolean {
  return hasPermission(canonical) || (legacy ? hasPermission(legacy) : false)
}

export function hasSalesDashboardViewPermission(hasPermission: HasPermissionFn): boolean {
  if (hasPermission(PERMISSIONS.SALES.DASHBOARD.VIEW)) {
    return true
  }

  return LEGACY_SALES_DASHBOARD_VIEW_PERMISSIONS.some((permission) => hasPermission(permission))
}

export function hasSalesDashboardExportPermission(hasPermission: HasPermissionFn): boolean {
  if (hasPermission(PERMISSIONS.SALES.DASHBOARD.EXPORT)) {
    return true
  }

  return LEGACY_SALES_DASHBOARD_EXPORT_PERMISSIONS.some((permission) => hasPermission(permission))
}

export function salesDashboardViewPermissionOptions(): string[] {
  return [PERMISSIONS.SALES.DASHBOARD.VIEW, ...LEGACY_SALES_DASHBOARD_VIEW_PERMISSIONS]
}

export function salesDashboardExportPermissionOptions(): string[] {
  return [PERMISSIONS.SALES.DASHBOARD.EXPORT, ...LEGACY_SALES_DASHBOARD_EXPORT_PERMISSIONS]
}

export interface SalesNavigationAccess {
  canViewDashboard: boolean
  canViewLeaderboard: boolean
  canViewOpenPipeline: boolean
  canViewSaleDetail: boolean
  canViewAmazonDashboard: boolean
  canViewInboundOrders: boolean
  canViewBudgets: boolean
  canViewTeamsAndReps: boolean
  canViewInquiriesDashboard: boolean
  canViewAmazonInquiries: boolean
  canViewChannelInquiries: boolean
  hasAnySalesDashboardSubPagesAccess: boolean
  hasAnySalesAdminAccess: boolean
  hasAnyInquiriesAccess: boolean
  hasAnySalesWorkflowAccess: boolean
  canViewWorkflowNewProduct: boolean
  canViewWorkflowModify: boolean
  canViewWorkflowProductBundle: boolean
  canViewWorkflowNewCustomerOrder: boolean
  canViewWorkflowDrafts: boolean
  canViewWorkflowSubmitted: boolean
  hasAnySalesModuleAccess: boolean
}

export function getSalesNavigationAccess(hasPermission: HasPermissionFn): SalesNavigationAccess {
  // Navigation follows the role picker (canonical names only). Deprecated legacy grants
  // from migrations must not expand the sidebar beyond what admins assign explicitly.
  const canViewDashboard = hasSalesDashboardViewPermission(hasPermission)
  const canViewLeaderboard = hasPermission(PERMISSIONS.SALES.LEADERBOARD.VIEW)
  const canViewOpenPipeline =
    isDashboardPageVisible('open-pipeline') &&
    hasNavigationItemPermission(hasPermission, PERMISSIONS.SALES.OPEN_PIPELINE.VIEW)
  const canViewSaleDetail = hasNavigationItemPermission(
    hasPermission,
    PERMISSIONS.SALES.SALES_DETAIL.VIEW,
  )
  const canViewAmazonDashboard = hasPermission(PERMISSIONS.SALES.AMAZON_DASHBOARD.VIEW)
  const canViewInboundOrders =
    isDashboardPageVisible('inbound-orders') &&
    hasPermission(PERMISSIONS.SALES.INBOUND_ORDERS.VIEW)
  const canViewBudgets = hasPermission(PERMISSIONS.SALES.BUDGETS.VIEW)
  const canViewTeamsAndReps = hasPermission(PERMISSIONS.SALES.TEAMS_REPS.VIEW)

  const canViewInquiriesDashboard =
    hasNavigationItemPermission(hasPermission, PERMISSIONS.INQUIRIES.VIEW) ||
    hasNavigationItemPermission(hasPermission, PERMISSIONS.SALES.INQUIRIES.VIEW) ||
    hasPermission(PERMISSIONS.SALES.INQUIRIES.CREATE)
  const canViewAmazonInquiries = hasNavigationItemPermission(
    hasPermission,
    PERMISSIONS.INQUIRIES.VIEW,
  )
  const canViewChannelInquiries = hasNavigationItemPermission(
    hasPermission,
    PERMISSIONS.SALES.INQUIRIES.VIEW,
  )

  const hasAnySalesDashboardSubPagesAccess =
    canViewDashboard ||
    canViewLeaderboard ||
    canViewOpenPipeline ||
    canViewSaleDetail ||
    canViewAmazonDashboard ||
    canViewInboundOrders

  const hasAnySalesAdminAccess = canViewBudgets || canViewTeamsAndReps

  const hasAnyInquiriesAccess =
    canViewInquiriesDashboard || canViewAmazonInquiries || canViewChannelInquiries

  const hasAnySalesWorkflowAccess = canAccessAnySalesWorkflow(hasPermission)
  const canViewWorkflowNewProduct = hasPermission(PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.VIEW)
  const canViewWorkflowModify = hasPermission(PERMISSIONS.SALES.WORKFLOW_MODIFY.VIEW)
  const canViewWorkflowProductBundle = hasPermission(PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.VIEW)
  const canViewWorkflowNewCustomerOrder = hasPermission(
    PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.VIEW,
  )
  const canViewWorkflowDrafts = canAccessMyDrafts(hasPermission)
  const canViewWorkflowSubmitted = canViewSubmitted(hasPermission)

  const hasAnySalesModuleAccess =
    hasAnySalesDashboardSubPagesAccess ||
    hasAnySalesAdminAccess ||
    hasAnyInquiriesAccess ||
    hasAnySalesWorkflowAccess

  return {
    canViewDashboard,
    canViewLeaderboard,
    canViewOpenPipeline,
    canViewSaleDetail,
    canViewAmazonDashboard,
    canViewInboundOrders,
    canViewBudgets,
    canViewTeamsAndReps,
    canViewInquiriesDashboard,
    canViewAmazonInquiries,
    canViewChannelInquiries,
    hasAnySalesDashboardSubPagesAccess,
    hasAnySalesAdminAccess,
    hasAnyInquiriesAccess,
    hasAnySalesWorkflowAccess,
    canViewWorkflowNewProduct,
    canViewWorkflowModify,
    canViewWorkflowProductBundle,
    canViewWorkflowNewCustomerOrder,
    canViewWorkflowDrafts,
    canViewWorkflowSubmitted,
    hasAnySalesModuleAccess,
  }
}

export interface SalesNavRouteTarget {
  path: string
  routeName?: string
  canAccess: (access: SalesNavigationAccess) => boolean
}

export const SALES_WORKFLOW_ROUTE_PERMISSION_BY_PATH = {
  ...SW_ROUTE_MAP,
  '/sales/workflow/drafts': PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.CREATE, // sentinel; drafts use canAccessMyDrafts
} as const

export const SALES_WORKFLOW_ALL_ROUTE_PERMISSIONS = [
  ...Object.values(SW_ROUTE_MAP),
  ...([
    PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.CREATE,
    PERMISSIONS.SALES.WORKFLOW_MODIFY.CREATE,
    PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.CREATE,
    PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.CREATE,
  ] as const),
]

/** @deprecated Use SALES_WORKFLOW_ROUTE_PERMISSION_BY_PATH per route. */
export const SALES_WORKFLOW_ROUTE_PERMISSIONS: string[] = [...SALES_WORKFLOW_ALL_ROUTE_PERMISSIONS]

export function canAccessSalesWorkflowRoutes(access: SalesNavigationAccess): boolean {
  return access.hasAnySalesWorkflowAccess
}

/** Route guard: any permission that shows the Sales section in the sidebar. */
export const SALES_MODULE_ROUTE_PERMISSIONS: string[] = [
  PERMISSIONS.SALES.DASHBOARD_OLD.VIEW,
  PERMISSIONS.SALES.PERFORMANCE.VIEW,
  PERMISSIONS.SALES.LEADERBOARD.VIEW,
  PERMISSIONS.SALES.OPEN_PIPELINE.VIEW,
  PERMISSIONS.SALES.SALES_DETAIL.VIEW,
  PERMISSIONS.SALES.BUDGETS.VIEW,
  PERMISSIONS.SALES.TEAMS_REPS.VIEW,
  PERMISSIONS.INQUIRIES.VIEW,
  PERMISSIONS.SALES.INQUIRIES.VIEW,
  PERMISSIONS.SALES.INQUIRIES.CREATE,
  PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.VIEW,
  PERMISSIONS.SALES.WORKFLOW_MODIFY.VIEW,
  PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.VIEW,
  PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.VIEW,
  PERMISSIONS.SALES.WORKFLOW_NEW_PRODUCT.CREATE,
  PERMISSIONS.SALES.WORKFLOW_MODIFY.CREATE,
  PERMISSIONS.SALES.WORKFLOW_PRODUCT_BUNDLE.CREATE,
  PERMISSIONS.SALES.WORKFLOW_NEW_CUSTOMER_ORDER.CREATE,
  PERMISSIONS.SALES.WORKFLOW_SUBMITTED.VIEW,
]

/** Sidebar order — first match wins for default redirects. */
export const SALES_NAV_ROUTE_TARGETS: SalesNavRouteTarget[] = [
  {
    path: '/sales/dashboard',
    routeName: 'SalesDashboard',
    canAccess: (access) => access.canViewDashboard,
  },
  {
    path: '/sales/leaderboard',
    routeName: 'SalesLeaderboard',
    canAccess: (access) => access.canViewLeaderboard,
  },
  {
    path: '/sales/open-pipeline',
    routeName: 'SalesOpenPipeline',
    canAccess: (access) => access.canViewOpenPipeline,
  },
  {
    path: '/sales/sale-detail',
    routeName: 'SalesSaleDetail',
    canAccess: (access) => access.canViewSaleDetail,
  },
  {
    path: '/sales/budgets',
    routeName: 'SalesBudgets',
    canAccess: (access) => access.canViewBudgets,
  },
  {
    path: '/sales/teams-and-reps',
    routeName: 'SalesTeamsAndReps',
    canAccess: (access) => access.canViewTeamsAndReps,
  },
  {
    path: '/inquiries',
    routeName: 'Inquiries',
    canAccess: (access) => access.canViewInquiriesDashboard,
  },
  {
    path: '/sales/workflow/new-product',
    routeName: 'SalesWorkflowNewProduct',
    canAccess: (access) => access.canViewWorkflowNewProduct,
  },
  {
    path: '/sales/workflow/modify',
    routeName: 'SalesWorkflowModify',
    canAccess: (access) => access.canViewWorkflowModify,
  },
  {
    path: '/sales/workflow/product-bundle',
    routeName: 'SalesWorkflowProductBundle',
    canAccess: (access) => access.canViewWorkflowProductBundle,
  },
  {
    path: '/sales/workflow/new-customer-order',
    routeName: 'SalesWorkflowNewCustomerOrder',
    canAccess: (access) => access.canViewWorkflowNewCustomerOrder,
  },
  {
    path: '/sales/workflow/drafts',
    routeName: 'SalesWorkflowDrafts',
    canAccess: (access) => access.canViewWorkflowDrafts,
  },
  {
    path: '/sales/workflow/submitted',
    routeName: 'SalesWorkflowSubmitted',
    canAccess: (access) => access.canViewWorkflowSubmitted,
  },
]

export const SALES_DASHBOARD_SUB_PAGE_TARGETS: SalesNavRouteTarget[] = [
  {
    path: '/sales/dashboard',
    routeName: 'SalesDashboard',
    canAccess: (access) => access.canViewDashboard,
  },
  {
    path: '/sales/leaderboard',
    routeName: 'SalesLeaderboard',
    canAccess: (access) => access.canViewLeaderboard,
  },
  {
    path: '/sales/open-pipeline',
    routeName: 'SalesOpenPipeline',
    canAccess: (access) => access.canViewOpenPipeline,
  },
  {
    path: '/sales/sale-detail',
    routeName: 'SalesSaleDetail',
    canAccess: (access) => access.canViewSaleDetail,
  },
  {
    path: '/sales/amazon-dashboard',
    routeName: 'SalesAmazonDashboard',
    canAccess: (access) => access.canViewAmazonDashboard,
  },
  {
    path: '/sales/inbound-orders',
    routeName: 'SalesInboundOrders',
    canAccess: (access) => access.canViewInboundOrders,
  },
]

export function getFirstAccessibleSalesRoute(hasPermission: HasPermissionFn): string | null {
  const access = getSalesNavigationAccess(hasPermission)
  return SALES_NAV_ROUTE_TARGETS.find((target) => target.canAccess(access))?.path ?? null
}

export function getFirstAccessibleSalesDashboardSubPageRoute(
  hasPermission: HasPermissionFn
): string | null {
  const access = getSalesNavigationAccess(hasPermission)
  return SALES_DASHBOARD_SUB_PAGE_TARGETS.find((target) => target.canAccess(access))?.path ?? null
}

export function getFirstAccessibleSalesDashboardSubPageRouteName(
  hasPermission: HasPermissionFn
): string | null {
  const access = getSalesNavigationAccess(hasPermission)
  return SALES_DASHBOARD_SUB_PAGE_TARGETS.find((target) => target.canAccess(access))?.routeName ?? null
}

/** Route meta helper: canonical + legacy view permissions for renamed pages. */
export function salesViewPermissionOptions(
  canonical: string,
  legacy?: string
): string[] {
  return legacy ? [canonical, legacy] : [canonical]
}
